// Shared helpers for boxed-expression views. Extracted here so that the
// views stay short and focused on layout + moddle mutation.

export function uid(prefix) {
  return prefix + '_' + Math.random().toString(36).slice(2, 9);
}

export function textOf(el) {
  if (!el) return '';
  if (typeof el.text === 'string') return el.text;
  return '';
}

// contenteditable span bound to a commit callback. Same pattern as the
// DecisionTable editor so the look & feel stays consistent.
export function makeEditable(initialValue, placeholder, commit, extraClass) {
  const span = document.createElement('span');
  span.className = 'epx-dmn-expr__editable' + (extraClass ? ' ' + extraClass : '');
  span.setAttribute('contenteditable', 'true');
  span.setAttribute('spellcheck', 'false');
  span.textContent = initialValue == null ? '' : String(initialValue);
  if (!initialValue) {
    span.classList.add('epx-dmn-expr__editable--empty');
    span.dataset.placeholder = placeholder || '';
  }
  span.addEventListener('focus', () => span.classList.remove('epx-dmn-expr__editable--empty'));
  span.addEventListener('blur', () => {
    const v = (span.textContent || '').trim();
    commit(v);
  });
  span.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); span.blur(); }
    if (e.key === 'Escape') { e.preventDefault(); span.textContent = initialValue || ''; span.blur(); }
  });
  return span;
}

// Create a new Expression moddle element of a given type with sensible
// defaults. Used when the user adds a row, column, entry, etc.
export function createExpression(moddle, type, attrs = {}) {
  switch (type) {
    case 'dmn:LiteralExpression':
      return moddle.create('dmn:LiteralExpression', { id: uid('LitExpr'), text: '', ...attrs });
    case 'dmn:Context':
      return moddle.create('dmn:Context', { id: uid('Context'), contextEntry: [], ...attrs });
    case 'dmn:List':
      return moddle.create('dmn:List', { id: uid('List'), elements: [], ...attrs });
    case 'dmn:Invocation':
      return moddle.create('dmn:Invocation', {
        id: uid('Invocation'),
        calledFunction: moddle.create('dmn:LiteralExpression', { id: uid('LitExpr'), text: '' }),
        binding: [],
        ...attrs,
      });
    case 'dmn:Relation':
      return moddle.create('dmn:Relation', { id: uid('Relation'), column: [], row: [], ...attrs });
    case 'dmn:FunctionDefinition':
      return moddle.create('dmn:FunctionDefinition', {
        id: uid('FnDef'),
        formalParameter: [],
        body: moddle.create('dmn:LiteralExpression', { id: uid('LitExpr'), text: '' }),
        kind: 'FEEL',
        ...attrs,
      });
    case 'epx:MachineLearningDecision':
      return moddle.create('epx:MachineLearningDecision', {
        id: uid('MlDecision'),
        modelRef: '',
        modelVersion: '',
        runtime: 'rest',
        endpoint: '',
        inputBindings: [],
        outputBindings: [],
        ...attrs,
      });
    default:
      throw new Error(`[epx-dmn/expression] cannot create expression of type '${type}'`);
  }
}

export function createInformationItem(moddle, { name = '', typeRef } = {}) {
  return moddle.create('dmn:InformationItem', { id: uid('Var'), name, typeRef });
}

export function createContextEntry(moddle, { name, typeRef, value } = {}) {
  const entry = moddle.create('dmn:ContextEntry', {
    id: uid('CEntry'),
    value: value || createExpression(moddle, 'dmn:LiteralExpression'),
  });
  if (name !== undefined) {
    entry.variable = createInformationItem(moddle, { name, typeRef });
  }
  return entry;
}

export function createBinding(moddle, { name = '', typeRef, bindingFormula } = {}) {
  return moddle.create('dmn:Binding', {
    parameter: createInformationItem(moddle, { name, typeRef }),
    bindingFormula: bindingFormula || createExpression(moddle, 'dmn:LiteralExpression'),
  });
}

export const EXPRESSION_TYPES = [
  { type: 'dmn:LiteralExpression', label: 'Literal Expression', short: 'Literal' },
  { type: 'dmn:Context',           label: 'Context',            short: 'Context' },
  { type: 'dmn:List',              label: 'List',               short: 'List' },
  { type: 'dmn:Invocation',        label: 'Invocation',         short: 'Invocation' },
  { type: 'dmn:Relation',          label: 'Relation',           short: 'Relation' },
  { type: 'dmn:FunctionDefinition',label: 'Function Definition',short: 'Function' },
];

// EPX-specific (non-standard DMN) expression types. Lives in a separate list
// so the standard set above stays clean for any consumer that wants only the
// spec-defined boxed expressions.
export const EPX_EXPRESSION_TYPES = [
  { type: 'epx:MachineLearningDecision', label: '🧠 Machine Learning', short: 'ML' },
];

// Full set of decision-logic types — includes DecisionTable alongside every
// boxed expression. Used by both editors' header switcher so a Decision can
// cross the table↔expression boundary from either view. EPX extensions are
// appended at the end (after the standard DMN entries) so the order in the UI
// reads "spec-first, extensions-last".
export const LOGIC_TYPES = [
  { type: 'dmn:DecisionTable',     label: 'Decision Table',     short: 'Table' },
  ...EXPRESSION_TYPES,
  ...EPX_EXPRESSION_TYPES,
];

export function getExpressionTypeLabel(type) {
  const t = LOGIC_TYPES.find((e) => e.type === type);
  return t ? t.short : (type || '').replace(/^dmn:/, '');
}
